import { filters, locationFilters } from './filters';

const propertyFiltersNotToCount = [
    'source',
    'destination',
    'fypMode',
    'offer',
    'order',
    'excludeDuplicates',
    'boundSW',
    'boundNE',
    'area',
    'viewmode',
    'locationFreeText',
    'geoSimpleSelect',
    'fypFilterIsModeInterchangable',
];

type CountResult = {
    count: number;
    inputs: Array<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>;
}

function countPropertyFilters( container: HTMLElement, namesToIgnore: Array<string> = [] ): CountResult {
    const filterNamesToIgnore = [
        ...propertyFiltersNotToCount,
        ...locationFilters.all(),
        ...namesToIgnore,
    ];

    const result: CountResult = {
        count: 0,
        inputs: [],
    };

    if ( !filterNamesToIgnore.includes( 'locationId' ) ) {
        const locationID: HTMLInputElement | null = container.querySelector( 'input[name=locationId]' );

        if ( locationID && locationID.value.length > 0 ) {
            result.count += 1;
            result.inputs = [
                ...result.inputs,
                locationID,
            ];
        }
    }

    if ( !filterNamesToIgnore.includes( 'radius' ) ) {
        const radius: HTMLSelectElement | null = container.querySelector( 'select[name=radius]' );

        if ( radius && radius.value.length > 0 ) {
            result.count += 1;
            result.inputs = [
                ...result.inputs,
                radius,
            ];
        }
    }

    const inputs: Array<HTMLInputElement | HTMLSelectElement> = Array.from( container.querySelectorAll( 'input,select' ) );

    const inputsToIgnore = [
        ...filterNamesToIgnore,
        'locationId',
        'radius',
    ];

    inputs.forEach( ( input ) => {
        if ( input.name === '' || inputsToIgnore.includes( input.name ) ) {
            return;
        }

        if ( input.dataset.includeInCount && input.dataset.includeInCount === 'false' ) {
            return;
        }

        if ( propertyFiltersNotToCount.includes( input.name ) ) {
            return;
        }

        if ( input.value.length === 0 ) {
            return;
        }

        switch ( input.type ) {
        case 'radio':
        case 'checkbox':
            input = input as HTMLInputElement;

            if ( !input.checked ) {
                return;
            }
            break;
        }

        result.count += 1;
        result.inputs = [
            ...result.inputs,
            input,
        ];
    } );

    return result;
}

const locationFiltersNotToCount = [
    'area',
    'radius',
    'fypMode',
    'geoSimpleSelect',
    'fypFilterIsModeInterchangable',
];

function countLocationFilters( container: HTMLElement, namesToIgnore: Array<string> = [] ): CountResult {
    const filterNamesToIgnore = [
        ...locationFiltersNotToCount,
        ...filters.all().filter( ( name: string ) => !locationFilters.all().includes( name ) ),
        ...namesToIgnore,
    ];

    const result: CountResult = {
        count: 0,
        inputs: [],
    };

    const selects: Array<HTMLSelectElement> = Array.from( container.querySelectorAll( 'select' ) );
    const activeSelects = selects.filter( ( select ) => {
        if ( select.value.length === 0 ) {
            return false;
        }
        if ( filterNamesToIgnore.includes( select.name ) ) {
            return false;
        }
        if ( select.closest( '[disabled]' ) !== null ) {
            return false;
        }
        if ( select.dataset.includeInCount && select.dataset.includeInCount === 'false' ) {
            return false;
        }
        if ( select.options[ select.selectedIndex ].dataset.includeInCount && select.options[ select.selectedIndex ].dataset.includeInCount === 'false' ) {
            return false;
        }
        return true;
    } );

    result.count += activeSelects.length;
    result.inputs = [
        ...result.inputs,
        ...activeSelects,
    ];

    const tagLikeFilters = [
        'sunHours',
        'rainDays',
        'windSpeeds',
        'beachTags',
        'beachCompositions',
        'beachSandTypes',
        'internationalSchoolCurriculums',
        'internationalSchoolAges',
        'internationalSchoolLanguages',
        'locationTags',
        'airportTags',
        'golfTags',
        'mustHaveTrainStation',
        'mustHaveAirport',
        'mustHaveHiking',
        'mustHaveSkiing',
        'mustHaveHospital',
        'beachRankGroups',
        'golfRankGroups',
        'hikingRankGroups',
        'skiRankGroups',
        'airportRankGroups',
        'hospitalRankGroups',
        'internationalSchoolRankGroups',
        'weatherMonths',
    ];

    tagLikeFilters.forEach( ( inputName ) => {
        const inputs = Array.from( container.querySelectorAll<HTMLInputElement>( `[name="${inputName}"]` ) );

        const selected: Array<HTMLInputElement> = inputs.filter( ( input: HTMLInputElement ) => {
            return input.closest( 'fieldset[disabled]' ) === null;
        } ).filter( ( input: HTMLInputElement ) => {
            if ( input.type === 'hidden' ) {
                return input.value.length > 0;
            }
            return input.checked;
        } );

        result.count += selected.length;
        result.inputs = [
            ...result.inputs,
            ...selected,
        ];
    } );

    return result;
}

class FilterCount {
    property: CountResult;
    location: CountResult;

    get total(): number {
        return this.property.count + this.location.count;
    }
}

function countFilters(
    form: HTMLElement,
    propertyFiltersToIgnore: Array<string> = [],
    locationFiltersToIgnore: Array<string> = [],
): FilterCount {
    const counts = new FilterCount();
    counts.location = countLocationFilters( form, locationFiltersToIgnore );
    counts.property = countPropertyFilters( form, propertyFiltersToIgnore );
    return counts;
}

export {
    countPropertyFilters,
    countLocationFilters,
    countFilters,
    CountResult,
    FilterCount,
};
